import React from "react";
import { BarRounded } from "@visx/shape";
import { TooltipWithBounds, useTooltip } from "@visx/tooltip";
import { TOOLTIP_STYLE } from "./chartTheme";
import useArrowFocus from "./useArrowFocus";
import useHideOnEscape from "./useHideOnEscape";

// Uma barra horizontal a 100%, por segmentos (spec do RPAA-286, decisao 12: barras empilhadas). Entre
// dois segmentos, 2 px da superficie; os 4 px arredondados so nas duas pontas da barra. Cada segmento
// e focavel e tem nome ("Escritas: 21 (55,3%)"). Os segmentos nao levam rotulo: e a legenda que diz
// o valor de cada um. A zona que reage ao rato e ao toque e mais alta do que a barra (PAD em cima e
// em baixo), com a largura do segmento, e e o primeiro filho de cada marca: o anel de foco desenha-se
// nela (index.css). Esse retangulo tem a altura do svg, e o svg nao corta o que passa das bordas
// (`overflow: visible`), senao o traco do anel ficava com 1 px nas pontas e em cima e em baixo. Os
// segmentos chegam na ordem fixa das chaves e sem zeros.
const HEIGHT = 20;
const PAD = 4;
export const STACKED_BAR_HEIGHT = HEIGHT + 2 * PAD;
const GAP = 2;

// Onde fica cada segmento: a largura sem os intervalos, repartida pelo valor.
export const placeSegments = (segments, width) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const usable = Math.max(0, width - GAP * (segments.length - 1));
  let left = 0;
  return segments.map((segment) => {
    const segmentWidth = total ? (segment.value / total) * usable : 0;
    const placement = { ...segment, x: left, width: segmentWidth };
    left += segmentWidth + GAP;
    return placement;
  });
};

const StackedBar = ({ segments, width, label, formatValue }) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const placed = placeSegments(segments, width);
  const { tooltipOpen, tooltipData, tooltipLeft, showTooltip, hideTooltip } = useTooltip();
  const { active, setActive, refFor, onKeyDown } = useArrowFocus(segments.length);
  useHideOnEscape(tooltipOpen, hideTooltip);

  const show = (segment, index) => {
    setActive(index);
    showTooltip({ tooltipData: segment, tooltipLeft: segment.x + segment.width / 2 });
  };

  return (
    <div className="relative">
      <svg width={width} height={STACKED_BAR_HEIGHT} role="group" aria-label={label} style={{ overflow: "visible" }}>
        {placed.map((segment, index) => (
          <g
            key={segment.key}
            ref={refFor(index)}
            data-chart-mark=""
            tabIndex={index === active ? 0 : -1}
            role="img"
            aria-label={`${segment.label}: ${formatValue(segment.value, total)}`}
            onFocus={() => show(segment, index)}
            onBlur={hideTooltip}
            onMouseEnter={() => show(segment, index)}
            onMouseLeave={hideTooltip}
            onKeyDown={(event) => onKeyDown(index, event)}
          >
            <rect x={segment.x} y={0} width={segment.width} height={STACKED_BAR_HEIGHT} fill="transparent" />
            <BarRounded
              x={segment.x}
              y={PAD}
              width={segment.width}
              height={HEIGHT}
              radius={4}
              left={index === 0}
              right={index === placed.length - 1}
              fill={segment.color}
            />
          </g>
        ))}
      </svg>
      {/* O TooltipWithBounds mede o pai direto: com o div da barra, que so tem a altura dela, a
          tooltip nunca cabia por baixo e virava para cima, por cima da barra. Este pai tem espaco por
          baixo da barra e a largura dela, para a tooltip abrir em baixo e continuar a virar junto da
          borda direita. O TooltipWithBounds so se mede ao montar, e passar de um segmento para outro
          sem sair da barra (ou com as setas) nao o desmonta: a `key` monta-o de novo em cada
          segmento, senao ficava com a largura e a viragem do anterior. */}
      {tooltipOpen && tooltipData && (
        <div
          key={tooltipData.key}
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: STACKED_BAR_HEIGHT + 48 }}
        >
          <TooltipWithBounds left={tooltipLeft} top={STACKED_BAR_HEIGHT + 4} applyPositionStyle style={TOOLTIP_STYLE}>
            <strong>{tooltipData.label}</strong>: {formatValue(tooltipData.value, total)}
          </TooltipWithBounds>
        </div>
      )}
    </div>
  );
};

export default StackedBar;
